import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Plus,
  Trash2,
  Play,
  Pause,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  FileText
} from 'lucide-react';

interface AOI {
  id: string;
  name: string;
  description?: string;
  geometry: {
    type: string;
    coordinates: number[][];
  };
  status: 'active' | 'paused' | 'error';
  lastCheck?: string;
  nextCheck?: string;
  changeCount?: number;
  created_at: string;
  metadata?: {
    vessel_count?: number;
    aircraft_count?: number;
    last_live_intel_at?: string;
    last_object_snapshot?: Record<string, number>;
    ml_score?: number;
    ml_assessment?: string;
    pre_activity_alert?: boolean;
    alert_signature?: string;
    change_percent?: number;
  };
}

interface AOIMonitoringPanelProps {
  onAOISelect?: (aoi: AOI) => void;
  onAOICreate?: () => void;
}

const AOIMonitoringPanel: React.FC<AOIMonitoringPanelProps> = ({
  onAOISelect,
  onAOICreate
}) => {
  const [aois, setAOIs] = useState<AOI[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAOI, setSelectedAOI] = useState<AOI | null>(null);
  const [checkingNow, setCheckingNow] = useState<string | null>(null);
  const [generatingReport, setGeneratingReport] = useState<string | null>(null);

  useEffect(() => {
    fetchAOIs();
  }, []);

  const fetchAOIs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/geoint/wayback/aois', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // API stores snake_case; map to camelCase for the interface
        const mapped = (data.aois || []).map((a: any) => ({
          ...a,
          lastCheck: a.last_check ?? a.lastCheck,
          nextCheck: a.next_check ?? a.nextCheck,
          changeCount: a.change_count ?? a.changeCount ?? 0,
        }));
        setAOIs(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch AOIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (aoi: AOI) => {
    try {
      const newStatus = aoi.status === 'active' ? 'paused' : 'active';
      const response = await fetch(`/api/geoint/wayback/aois/${aoi.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        await fetchAOIs();
      }
    } catch (error) {
      console.error('Failed to update AOI status:', error);
    }
  };

  const handleDelete = async (aoiId: string) => {
    if (!confirm('Are you sure you want to delete this AOI?')) return;
    try {
      const response = await fetch(`/api/geoint/wayback/aois/${aoiId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token')}`
        }
      });
      if (response.ok) {
        await fetchAOIs();
      }
    } catch (error) {
      console.error('Failed to delete AOI:', error);
    }
  };

  const handleCheckNow = async (aoi: AOI) => {
    setCheckingNow(aoi.id);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token');
      const response = await fetch(`/api/geoint/wayback/aois/${aoi.id}/check-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTimeout(() => fetchAOIs(), 3000);
      }
    } catch (error) {
      console.error('Failed to trigger check:', error);
    } finally {
      setCheckingNow(null);
    }
  };

  const handleGenerateReport = async (aoi: AOI) => {
    setGeneratingReport(aoi.id);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token');
      const response = await fetch(`/api/geoint/wayback/aois/${aoi.id}/report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AOI_Report_${aoi.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setGeneratingReport(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" />
          AOI Monitoring
        </span>
        <button
          onClick={onAOICreate}
          className="flex items-center gap-1 px-2 py-1 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-xs font-medium transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add AOI
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        </div>
      ) : aois.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          <MapPin className="w-6 h-6 mx-auto mb-1 opacity-40" />
          <p className="text-xs">No AOIs configured</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {aois.map((aoi) => (
            <div
              key={aoi.id}
              className={`p-2.5 border rounded cursor-pointer transition-colors ${
                selectedAOI?.id === aoi.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => {
                setSelectedAOI(aoi);
                onAOISelect?.(aoi);
              }}
            >
              {/* Card header row */}
              <div className="flex items-center gap-1.5">
                {getStatusIcon(aoi.status)}
                <span className="text-xs font-semibold text-foreground flex-1 truncate">{aoi.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                  aoi.status === 'active'
                    ? 'bg-green-900/50 text-green-400'
                    : aoi.status === 'paused'
                    ? 'bg-yellow-900/50 text-yellow-400'
                    : 'bg-red-900/50 text-red-400'
                }`}>
                  {aoi.status}
                </span>
                {/* Action buttons */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); handleCheckNow(aoi); }} disabled={checkingNow === aoi.id}
                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-50" title="Check now">
                    {checkingNow === aoi.id ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : <RefreshCw className="w-3 h-3 text-muted-foreground" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleGenerateReport(aoi); }} disabled={generatingReport === aoi.id}
                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-50" title="Generate report">
                    {generatingReport === aoi.id ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : <FileText className="w-3 h-3 text-muted-foreground" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleToggleStatus(aoi); }}
                    className="p-1 hover:bg-muted rounded transition-colors" title={aoi.status === 'active' ? 'Pause' : 'Resume'}>
                    {aoi.status === 'active' ? <Pause className="w-3 h-3 text-muted-foreground" /> : <Play className="w-3 h-3 text-muted-foreground" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(aoi.id); }}
                    className="p-1 hover:bg-destructive/10 rounded transition-colors" title="Delete">
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </button>
                </div>
              </div>

              {/* Timestamps row */}
              <div className="flex gap-3 text-[10px] text-muted-foreground mt-1">
                <span>Last: {formatDate(aoi.lastCheck)}</span>
                {aoi.changeCount !== undefined && aoi.changeCount > 0 && (
                  <span className="text-primary font-medium">{aoi.changeCount} changes</span>
                )}
              </div>

              {/* Metadata summary — single compact row */}
              {aoi.metadata && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {aoi.metadata.change_percent !== undefined && aoi.metadata.change_percent !== null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${aoi.metadata.change_percent >= 15 ? 'bg-red-900/40 text-red-400' : aoi.metadata.change_percent >= 5 ? 'bg-orange-900/40 text-orange-400' : 'bg-muted text-muted-foreground'}`}>
                      {aoi.metadata.change_percent.toFixed(1)}% chg
                    </span>
                  )}
                  {(aoi.metadata.vessel_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-900/40 text-cyan-400">{aoi.metadata.vessel_count}v AIS</span>
                  )}
                  {(aoi.metadata.aircraft_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-900/40 text-orange-400">{aoi.metadata.aircraft_count}ac</span>
                  )}
                  {aoi.metadata.last_object_snapshot && Object.entries(aoi.metadata.last_object_snapshot).map(([cls, n]) => (
                    <span key={cls} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-900/40 text-violet-400">{n} {cls}</span>
                  ))}
                  {aoi.metadata.ml_score !== undefined && aoi.metadata.ml_score !== null && (() => {
                    const score = aoi.metadata.ml_score!;
                    const label = score >= 0.75 ? 'CRIT' : score >= 0.55 ? 'HIGH' : score >= 0.35 ? 'ELEV' : 'NORM';
                    const cls = score >= 0.75 ? 'bg-red-900/40 text-red-400' : score >= 0.55 ? 'bg-orange-900/40 text-orange-400' : score >= 0.35 ? 'bg-yellow-900/40 text-yellow-400' : 'bg-muted text-muted-foreground';
                    return <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{label} {Math.round(score * 100)}%</span>;
                  })()}
                  {aoi.metadata.pre_activity_alert && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/60 text-red-300 flex items-center gap-0.5">
                      <AlertCircle className="w-2.5 h-2.5" />{aoi.metadata.alert_signature || 'Alert'}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AOIMonitoringPanel;

